// Client-side store for Phase 1. Loads lever definitions + the BNA feed once, then re-ranks
// entirely in the browser as sliders move — using the SAME shared math the Worker runs, so
// the edge and the browser can never disagree. Slider moves are also persisted to the Worker
// (debounced) so the shared posture survives a reload and is visible to other operators.
import { rankBnas, type LeverState, type RankedBna } from '../../../shared/priority';

export interface Lever {
  id: string;
  name: string;
  leftPoleLabel: string;
  rightPoleLabel: string;
  weight: number;
  position: number; // 0..100, current shared value
}

export interface Bna {
  id: string;
  unitId: string;
  title: string;
  description: string | null;
  baseUrgency: number;
  scores: Record<string, number>;
}

let levers: Lever[] = [];
let bnas: Bna[] = [];
let loadError: string | null = null;
const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();

export function getLevers(): Lever[] {
  return levers;
}

export function getLoadError(): string | null {
  return loadError;
}

/** Fetch levers + BNAs once. Safe to call again to refresh. */
export async function loadData(): Promise<void> {
  loadError = null;
  try {
    const [leversRes, bnasRes] = await Promise.all([fetch('/api/levers'), fetch('/api/bnas')]);
    if (!leversRes.ok || !bnasRes.ok) {
      throw new Error(`api error (levers ${leversRes.status}, bnas ${bnasRes.status})`);
    }
    levers = ((await leversRes.json()) as { levers: Lever[] }).levers;
    bnas = ((await bnasRes.json()) as { bnas: Bna[] }).bnas;
  } catch (e) {
    loadError = e instanceof Error ? e.message : String(e);
  }
}

function leverState(): LeverState[] {
  return levers.map((l) => ({ id: l.id, weight: l.weight, position: l.position }));
}

/** Ranked BNAs for one unit against the current posture. Pure, instant — no network. */
export function rankedForUnit(unitId: string): RankedBna<Bna>[] {
  return rankBnas(
    bnas.filter((b) => b.unitId === unitId),
    leverState(),
  );
}

/** Top-N ranked BNAs across all units (for the command-center tiles). */
export function topRankedForUnit(unitId: string, n: number): RankedBna<Bna>[] {
  return rankedForUnit(unitId).slice(0, n);
}

/**
 * Update a lever locally (instant re-rank) and persist to the Worker, debounced so dragging
 * a slider doesn't fire a write per pixel. Returns immediately; persistence is best-effort.
 */
export function setPosition(leverId: string, position: number): void {
  const lever = levers.find((l) => l.id === leverId);
  if (!lever) return;
  lever.position = position;

  const existing = saveTimers.get(leverId);
  if (existing) clearTimeout(existing);
  saveTimers.set(
    leverId,
    setTimeout(() => {
      saveTimers.delete(leverId);
      void fetch(`/api/levers/${encodeURIComponent(leverId)}/position`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ position }),
      }).catch(() => {
        /* best-effort; the local posture still drives the live ranking */
      });
    }, 250),
  );
}
